import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Truck, Package, AlertTriangle } from 'lucide-react';
import AddressSelector from '@/components/AddressSelector';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { calculateOrderShipping } from '@/lib/shippingUtils';
import { MINIMUM_ORDER_FOR_DELIVERY } from '@/lib/shippingConfig';
import type { ShippingOption } from '@/lib/shippingConfig';
import { useIsMobile } from '@/hooks/use-mobile';
import { useShippingSettings } from '@/hooks/useShippingSettings';

interface CheckoutShippingStepProps {
  onNext: () => void;
  onBack: () => void;
  onShippingChange: (option: ShippingOption, city: string | null, cost: number, addressId?: string, addressDetails?: any) => void;
  selectedOption: ShippingOption | null;
  selectedCity: string | null;
  selectedAddressId?: string;
  subtotal: number;
}

const CheckoutShippingStep = ({
  onNext,
  onBack,
  onShippingChange,
  selectedOption,
  selectedCity,
  selectedAddressId,
  subtotal,
}: CheckoutShippingStepProps) => {
  const { user, profile } = useAuth();
  const isMobile = useIsMobile();
  const addressSectionRef = useRef<HTMLDivElement>(null);
  const [deliveryOption, setDeliveryOption] = useState<'pickup' | 'delivery' | null>(
    selectedOption === 'pickup' ? 'pickup' : selectedOption ? 'delivery' : null
  );
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>(selectedAddressId);
  const [shippingCost, setShippingCost] = useState(0);
  const [currentCity, setCurrentCity] = useState<string | null>(selectedCity);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const { settings: shippingSettings } = useShippingSettings();

  const priceOverrides = {
    central: shippingSettings.centralPrice,
    remote: shippingSettings.remotePrice
  };

  const canSelectDelivery = subtotal >= MINIMUM_ORDER_FOR_DELIVERY;
  const amountToReachMinimum = MINIMUM_ORDER_FOR_DELIVERY - subtotal;

  useEffect(() => {
    if (user) {
      fetchAddresses();
    }
  }, [user]);

  useEffect(() => {
    // Calculate shipping cost when city changes (always regular delivery)
    if (deliveryOption === 'delivery' && currentCity) {
      const { cost } = calculateOrderShipping('delivery', currentCity, priceOverrides);
      setShippingCost(cost);
    } else if (deliveryOption === 'pickup') {
      setShippingCost(0);
    }
  }, [currentCity, deliveryOption]);

  // Auto-scroll to address section when delivery is selected on mobile
  useEffect(() => {
    if (isMobile && deliveryOption === 'delivery' && addressSectionRef.current) {
      setTimeout(() => {
        const element = addressSectionRef.current;
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 300);
    }
  }, [deliveryOption, isMobile]);

  const fetchAddresses = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('addresses')
        .select('*')
        .eq('user_id', user?.id)
        .order('last_used_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAddresses(data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const handleGuestAddressSave = (address: any) => {
    // For guest users, we manage the address list locally
    setAddresses(prev => {
      // Check if address with same ID exists (update)
      const exists = prev.some(a => a.id === address.id);
      if (exists) {
        return prev.map(a => a.id === address.id ? address : a);
      }
      return [address, ...prev];
    });

    // Auto select the new guest address
    handleAddressSelect(address.id, address);
  };

  const handleAddressSelect = (addressId: string, directAddressObject?: any) => {
    // Handle clearing selection
    if (!addressId || addressId === '') {
      setSelectedAddress(undefined);
      setCurrentCity(null);
      onShippingChange('delivery', null, 0);
      return;
    }

    setSelectedAddress(addressId);

    // Find address in list OR use the directly provided object (useful for just-saved guest address)
    const address = directAddressObject || addresses.find(a => a.id === addressId);

    if (address) {
      setCurrentCity(address.city);
      // Auto-calculate regular delivery cost
      const { cost } = calculateOrderShipping('delivery', address.city, priceOverrides);
      setShippingCost(cost);
      onShippingChange('delivery', address.city, cost, addressId, address);
    }
  };

  const handleDeliveryOptionChange = (option: 'pickup' | 'delivery') => {
    if (option === 'delivery' && !canSelectDelivery) {
      toast.error("מינימום הזמנה למשלוח", {
        description: `מינימום הזמנה למשלוח הוא ₪${MINIMUM_ORDER_FOR_DELIVERY.toLocaleString()}. נותר להשלים ₪${amountToReachMinimum.toLocaleString()}`,
      });
      return;
    }

    setDeliveryOption(option);
    if (option === 'pickup') {
      onShippingChange('pickup', null, 0);
    } else {
      setSelectedAddress(undefined);
      setCurrentCity(null);
    }
  };

  const handleNext = () => {
    if (isEditingAddress) {
      toast.error("סיים עריכת כתובת", {
        description: "אנא שמור או בטל את עריכת הכתובת לפני מעבר לתשלום",
      });
      return;
    }

    if (!deliveryOption) {
      toast.error("בחר אופציית משלוח", {
        description: "אנא בחר איסוף עצמי או משלוח",
      });
      return;
    }

    if (deliveryOption === 'delivery') {
      if (!selectedAddress) {
        toast.error("בחר כתובת", {
          description: "אנא בחר כתובת משלוח",
        });
        return;
      }
    }

    onNext();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Minimum Order Warning - Removed and moved to card */}

      {/* Pickup or Delivery Selection */}
      <Card>
        <CardContent className="pt-6" dir="rtl">
          <h3 className="text-lg font-semibold mb-4 text-right">
            {profile?.first_name ? `היי ${profile.first_name}, ` : ''}בחר את אופן המשלוח
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
            {/* Pickup Card */}
            <Card
              className={`cursor-pointer transition-all ${deliveryOption === 'pickup'
                ? 'border-2 border-lime bg-lime/5'
                : 'border hover:border-lime/50'
                }`}
              onClick={() => handleDeliveryOptionChange('pickup')}
              role="button"
              aria-pressed={deliveryOption === 'pickup'}
            >
              <CardContent className="p-6" dir="rtl">
                <div className="space-y-2 text-right">
                  <div className="flex items-center gap-2 w-full">
                    <Package className="h-5 w-5" />
                    <span className="text-lg font-medium">איסוף עצמי</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    איסוף עצמי מגנות הדר. יתואם תוך 48 שעות ממועד ההזמנה
                  </p>
                  <p className="text-lg font-bold text-lime mt-2">חינם</p>
                </div>
              </CardContent>
            </Card>

            {/* Delivery Card */}
            <Card
              className={`transition-all ${!canSelectDelivery
                ? 'opacity-50 cursor-not-allowed border-muted'
                : deliveryOption === 'delivery'
                  ? 'cursor-pointer border-2 border-lime bg-lime/5'
                  : 'cursor-pointer border hover:border-lime/50'
                }`}
              onClick={() => handleDeliveryOptionChange('delivery')}
              role="button"
              aria-pressed={deliveryOption === 'delivery'}
              aria-disabled={!canSelectDelivery}
            >
              <CardContent className="p-6" dir="rtl">
                <div className="space-y-2 text-right">
                  <div className="flex items-center gap-2 w-full">
                    <Truck className="h-5 w-5" />
                    <span className="text-lg font-medium">משלוח</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    משלוח לכתובת שתבחר (עד 14 ימי עבודה)
                  </p>
                  {!canSelectDelivery && (
                    <div className="text-sm text-orange-600 font-medium mt-2 space-y-1">
                      <p>מינימום הזמנה למשלוח: ₪{MINIMUM_ORDER_FOR_DELIVERY.toLocaleString()}</p>
                      <p>נותר להשלים ₪{amountToReachMinimum.toLocaleString()} כדי לאפשר אופציית משלוח.</p>
                      <p>כרגע זמין רק <strong>איסוף עצמי</strong>.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Address Section (shown only if delivery is selected) */}
      {deliveryOption === 'delivery' && (
        <div className="space-y-6">
          <Card ref={addressSectionRef}>
            <CardContent className="pt-6">
              <AddressSelector
                addresses={addresses}
                selectedId={selectedAddress || null}
                onSelect={handleAddressSelect}
                onAddressesChange={fetchAddresses}
                title="בחר כתובת למשלוח"
                type="shipping"
                showAddNew={true}
                onCityChange={(city) => {
                  setCurrentCity(city);
                  const { cost } = calculateOrderShipping('delivery', city, priceOverrides);
                  setShippingCost(cost);
                  onShippingChange('delivery', city, cost, selectedAddress);
                }}
                onEditingChange={setIsEditingAddress}
                onGuestAddressSave={handleGuestAddressSave}
              />
            </CardContent>
          </Card>

          {/* Shipping Cost Display (shown after address is selected) */}
          {selectedAddress && currentCity && !isEditingAddress && (
            <Card>
              <CardContent className="pt-6" dir="rtl">
                <h3 className="text-lg font-semibold mb-4 text-right">עלות משלוח</h3>
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">משלוח רגיל</span>
                    <span className="text-xl font-bold">{formatPrice(shippingCost)}</span>
                  </div>
                  <Alert className="border-border bg-muted/50 [&>svg]:left-auto [&>svg]:right-4 [&>svg~*]:pl-0 [&>svg~*]:pr-7">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    <AlertDescription className="text-muted-foreground text-sm">
                      <strong>פריקה על שפת המדרכה בלבד - ללא שינוע!</strong>
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4 items-center">
        <button
          onClick={onBack}
          className="text-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
        >
          <span className="text-sm">&lt;</span>
          <span>חזור</span>
        </button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-lime text-lime-foreground hover:bg-lime/90"
          disabled={
            isEditingAddress ||
            !deliveryOption ||
            (deliveryOption === 'delivery' && !selectedAddress)
          }
        >
          המשך לתשלום
        </Button>
      </div>
    </div>
  );
};

export default CheckoutShippingStep;